import { StateCreator } from 'zustand';

import {
  insertPersonInOrder,
  PersonPlacement,
  personSortKey,
  resolvePersonPlacement,
} from './peopleOrder';
import { CONTACT_SCOPE, ContactsState, ContactsStore } from './types';
import {
  MAX_LOADED_MESSAGES,
  MessagesFilter,
} from '../../pages/private/Contacts/contacts.constants';
import { buildMessageActivityKey } from '../../pages/private/Contacts/utils/messageActivityKey';
import {
  Person,
  PersonActivity,
  PersonEvent,
} from '../../services/crm.service';

const computeHasMorePage = <T>(cursor: string | null, items: T[]): boolean =>
  cursor !== null && items.length > 0;

const computeHasMoreMessagesPage = <T>(
  cursor: string | null,
  items: T[],
  totalLoaded: number,
): boolean =>
  computeHasMorePage(cursor, items) && totalLoaded < MAX_LOADED_MESSAGES;

const computeMessagesCapped = <T>(
  cursor: string | null,
  items: T[],
  totalLoaded: number,
): boolean =>
  computeHasMorePage(cursor, items) && totalLoaded >= MAX_LOADED_MESSAGES;

const replaceSurvivorAndRemoveLosers = (
  people: Person[],
  survivor: Person,
  mergedIds: string[],
): Person[] => {
  const mergedIdSet = new Set(mergedIds);
  return people
    .filter((person) => !mergedIdSet.has(person.id))
    .map((person) => (person.id === survivor.id ? survivor : person));
};

const resolveDeepLinkAfterUpsert = (
  deepLinkPerson: Person | null,
  person: Person,
): Person | null =>
  deepLinkPerson?.id === person.id ? person : deepLinkPerson;

const resolveDeepLinkAfterRemoval = (
  deepLinkPerson: Person | null,
  personId: string,
): Person | null => (deepLinkPerson?.id === personId ? null : deepLinkPerson);

const resolveDeepLinkAfterMerge = (
  deepLinkPerson: Person | null,
  mergedIds: string[],
): Person | null =>
  deepLinkPerson && mergedIds.includes(deepLinkPerson.id)
    ? null
    : deepLinkPerson;

const dedupeIncomingMessages = (
  existing: PersonActivity[],
  incoming: PersonActivity[],
): PersonActivity[] => {
  const existingKeys = new Set(
    existing.map((message) =>
      buildMessageActivityKey(message.createdAt, message.messageId),
    ),
  );
  return incoming.filter(
    (message) =>
      !existingKeys.has(
        buildMessageActivityKey(message.createdAt, message.messageId),
      ),
  );
};

const dedupeIncomingEvents = (
  existing: PersonEvent[],
  incoming: PersonEvent[],
): PersonEvent[] => {
  const existingIds = new Set(existing.map((event) => event.id));
  return incoming.filter((event) => !existingIds.has(event.id));
};

const addUniqueKeys = (current: string[], incoming: string[]): string[] =>
  Array.from(new Set([...current, ...incoming]));

const insertAtPlacement = (
  people: Person[],
  person: Person,
  placement: PersonPlacement,
): Person[] =>
  placement.kind === 'insert'
    ? [
        ...people.slice(0, placement.index),
        person,
        ...people.slice(placement.index),
      ]
    : people;

const insertIntoSearchResults = (
  searchResults: Person[] | null,
  person: Person,
): Person[] | null =>
  searchResults ? insertPersonInOrder(searchResults, person) : searchResults;

const repositionInSearchResults = (
  searchResults: Person[] | null,
  person: Person,
): Person[] | null => {
  if (!searchResults) {
    return searchResults;
  }
  const existing = searchResults.find((p) => p.id === person.id);
  if (!existing) {
    return searchResults;
  }
  if (personSortKey(existing) === personSortKey(person)) {
    return searchResults.map((p) => (p.id === person.id ? person : p));
  }
  return insertPersonInOrder(
    searchResults.filter((p) => p.id !== person.id),
    person,
  );
};

const upsertPersonInPeople = (
  people: Person[],
  person: Person,
  hasMore: boolean,
) => {
  const existing = people.find((p) => p.id === person.id);
  if (existing && personSortKey(existing) === personSortKey(person)) {
    return {
      people: people.map((p) => (p.id === person.id ? person : p)),
      isBeyondWindow: false,
    };
  }

  const withoutExisting = people.filter((p) => p.id !== person.id);
  const placement = resolvePersonPlacement(withoutExisting, person, hasMore);
  return {
    people: insertAtPlacement(withoutExisting, person, placement),
    isBeyondWindow: placement.kind === 'beyondWindow',
  };
};

const INITIAL_CONTACTS_STATE: ContactsState = {
  scope: CONTACT_SCOPE.PERSONAL,
  query: '',
  originFilter: 'all',
  people: [],
  cursor: null,
  hasMore: false,
  isFetchingMore: false,
  isLoadingPeople: false,
  peopleError: null,
  searchResults: null,
  isSearching: false,
  searchError: null,
  selectedPersonId: null,
  messages: [],
  messagesPersonId: null,
  messagesCursor: null,
  messagesGeneration: 0,
  hasMoreMessages: false,
  messagesCapped: false,
  isLoadingMessages: false,
  isLoadingMoreMessages: false,
  messagesFilter: MessagesFilter.ALL,
  recentlyInsertedMessageKeys: [],
  removedMessageKeys: [],
  events: [],
  eventsPersonId: null,
  eventsCursor: null,
  eventsGeneration: 0,
  hasMoreEvents: false,
  isLoadingEvents: false,
  isLoadingMoreEvents: false,
  recentlyInsertedEventIds: [],
  removedEventIds: [],
  locallyRemovedEventIds: [],
  notesStatus: 'idle',
  isCreateOpen: false,
  editPersonId: null,
  deleteTargetId: null,
  deepLinkPerson: null,
  isResolvingDeepLink: false,
};

export const createContactsSlice: StateCreator<ContactsStore> = (set) => ({
  ...INITIAL_CONTACTS_STATE,

  setScope: (scope) => set({ scope }),

  setQuery: (query) => set({ query }),

  setOriginFilter: (originFilter) => set({ originFilter }),

  setPeoplePage: (people, cursor) =>
    set({ people, cursor, hasMore: cursor !== null }),

  appendPeoplePage: (people, cursor) =>
    set((state) => {
      const existingIds = new Set(state.people.map((p) => p.id));
      const deduped = people.filter((p) => !existingIds.has(p.id));
      return {
        people: [...state.people, ...deduped],
        cursor,
        hasMore: cursor !== null,
      };
    }),

  setFetchingMore: (isFetchingMore) => set({ isFetchingMore }),

  resetList: () =>
    set({
      people: [],
      cursor: null,
      hasMore: false,
      isFetchingMore: false,
      isLoadingPeople: false,
      peopleError: null,
    }),

  setLoadingPeople: (isLoadingPeople) => set({ isLoadingPeople }),

  setPeopleError: (peopleError) => set({ peopleError }),

  setSearchResults: (searchResults) => set({ searchResults }),

  setSearching: (isSearching) => set({ isSearching }),

  setSearchError: (searchError) => set({ searchError }),

  clearSearch: () =>
    set({ searchResults: null, isSearching: false, searchError: null }),

  selectPerson: (personId) =>
    set({
      selectedPersonId: personId,
      recentlyInsertedMessageKeys: [],
      removedMessageKeys: [],
      recentlyInsertedEventIds: [],
      removedEventIds: [],
      locallyRemovedEventIds: [],
    }),

  upsertPerson: (person) =>
    set((state) => {
      const { people, isBeyondWindow } = upsertPersonInPeople(
        state.people,
        person,
        state.hasMore,
      );
      const shouldPromoteDeepLink =
        isBeyondWindow && state.selectedPersonId === person.id;

      return {
        people,
        searchResults: repositionInSearchResults(state.searchResults, person),
        deepLinkPerson: shouldPromoteDeepLink
          ? person
          : resolveDeepLinkAfterUpsert(state.deepLinkPerson, person),
      };
    }),

  applyCreatedPerson: (person) =>
    set((state) => {
      const placement = resolvePersonPlacement(
        state.people,
        person,
        state.hasMore,
      );

      return {
        people: insertAtPlacement(state.people, person, placement),
        searchResults: insertIntoSearchResults(state.searchResults, person),
        selectedPersonId: person.id,
        deepLinkPerson:
          placement.kind === 'beyondWindow'
            ? person
            : resolveDeepLinkAfterUpsert(state.deepLinkPerson, person),
      };
    }),

  removePerson: (personId) =>
    set((state) => ({
      people: state.people.filter((p) => p.id !== personId),
      searchResults: state.searchResults
        ? state.searchResults.filter((p) => p.id !== personId)
        : state.searchResults,
      selectedPersonId:
        state.selectedPersonId === personId ? null : state.selectedPersonId,
      deepLinkPerson: resolveDeepLinkAfterRemoval(
        state.deepLinkPerson,
        personId,
      ),
    })),

  applyMergeResult: (person, mergedIds) =>
    set((state) => ({
      people: replaceSurvivorAndRemoveLosers(state.people, person, mergedIds),
      searchResults: state.searchResults
        ? replaceSurvivorAndRemoveLosers(state.searchResults, person, mergedIds)
        : state.searchResults,
      deepLinkPerson: resolveDeepLinkAfterMerge(
        state.deepLinkPerson,
        mergedIds,
      ),
    })),

  setMessages: (personId, items, cursor = null) =>
    set((state) => ({
      messages: items,
      messagesPersonId: personId,
      messagesCursor: cursor,
      messagesGeneration: state.messagesGeneration + 1,
      hasMoreMessages: computeHasMoreMessagesPage(cursor, items, items.length),
      messagesCapped: computeMessagesCapped(cursor, items, items.length),
    })),

  appendMessages: (personId, generation, items, cursor) =>
    set((state) => {
      if (
        personId !== state.messagesPersonId ||
        generation !== state.messagesGeneration
      ) {
        return {};
      }

      const messages = [...state.messages, ...items];

      return {
        messages,
        messagesCursor: cursor,
        hasMoreMessages: computeHasMoreMessagesPage(
          cursor,
          items,
          messages.length,
        ),
        messagesCapped: computeMessagesCapped(cursor, items, messages.length),
      };
    }),

  // Never bumps messagesGeneration: that would invalidate an appendMessages already in flight.
  prependMessages: (personId, items) =>
    set((state) => {
      if (personId !== state.messagesPersonId) {
        return {};
      }

      // Drop tombstoned keys first: a stale in-flight refetch response landing after
      // removeMessage ran must not resurrect a message the user already deleted.
      const notRemoved = items.filter(
        (message) =>
          !state.removedMessageKeys.includes(
            buildMessageActivityKey(message.createdAt, message.messageId),
          ),
      );

      const newMessages = dedupeIncomingMessages(state.messages, notRemoved);
      if (newMessages.length === 0) {
        return {};
      }

      const messages = [...newMessages, ...state.messages].sort(
        (a, b) => b.createdAt - a.createdAt,
      );

      // computeHasMoreMessagesPage/computeMessagesCapped expect a freshly-fetched
      // page in `items`; a prepend merges into the already-loaded list, so recompute
      // only the ceiling half here and keep whatever pagination already decided.
      // hasMoreBeyondLoaded is the single source of truth for "backend has more beyond
      // what's loaded" — this and the removeMessage reconstruction are the only writers
      // that derive hasMoreMessages/messagesCapped from an existing pair rather than a
      // fresh page, and together they deliberately keep the two bits mutually exclusive.
      const hasMoreBeyondLoaded = state.hasMoreMessages || state.messagesCapped;

      return {
        messages,
        hasMoreMessages:
          hasMoreBeyondLoaded && messages.length < MAX_LOADED_MESSAGES,
        messagesCapped:
          hasMoreBeyondLoaded && messages.length >= MAX_LOADED_MESSAGES,
      };
    }),

  setLoadingMessages: (isLoadingMessages) => set({ isLoadingMessages }),

  setLoadingMoreMessages: (isLoadingMoreMessages) =>
    set({ isLoadingMoreMessages }),

  setMessagesFilter: (messagesFilter) =>
    set((state) => ({
      messagesFilter,
      messages: [],
      messagesCursor: null,
      messagesGeneration: state.messagesGeneration + 1,
      hasMoreMessages: false,
      messagesCapped: false,
      removedMessageKeys: [],
    })),

  markMessagesArrived: (keys) =>
    set((state) => ({
      recentlyInsertedMessageKeys: addUniqueKeys(
        state.recentlyInsertedMessageKeys,
        keys,
      ),
    })),

  clearMessageArrived: (key) =>
    set((state) => ({
      recentlyInsertedMessageKeys: state.recentlyInsertedMessageKeys.filter(
        (existingKey) => existingKey !== key,
      ),
    })),

  removeMessage: (personId, messageKey) =>
    set((state) => {
      if (personId !== state.messagesPersonId) {
        return {};
      }

      const messages = state.messages.filter(
        (message) =>
          buildMessageActivityKey(message.createdAt, message.messageId) !==
          messageKey,
      );
      if (messages.length === state.messages.length) {
        return {};
      }

      const hasMoreBeyondLoaded = state.hasMoreMessages || state.messagesCapped;

      return {
        messages,
        recentlyInsertedMessageKeys: state.recentlyInsertedMessageKeys.filter(
          (existingKey) => existingKey !== messageKey,
        ),
        removedMessageKeys: addUniqueKeys(state.removedMessageKeys, [
          messageKey,
        ]),
        // hasMoreMessages/messagesCapped are kept mutually exclusive by construction,
        // not by accident across every writer: prependMessages and this reconstruction
        // are the two places that derive the pair from hasMoreBeyondLoaded, so the OR
        // below reconstructs the "backend has more" bit intact — a removal never changes it.
        hasMoreMessages:
          hasMoreBeyondLoaded && messages.length < MAX_LOADED_MESSAGES,
        messagesCapped:
          hasMoreBeyondLoaded && messages.length >= MAX_LOADED_MESSAGES,
      };
    }),

  setEvents: (personId, items, cursor) =>
    set((state) => ({
      events: items,
      eventsPersonId: personId,
      eventsCursor: cursor,
      eventsGeneration: state.eventsGeneration + 1,
      hasMoreEvents: computeHasMorePage(cursor, items),
    })),

  appendEvents: (personId, generation, items, cursor) =>
    set((state) => {
      if (
        personId !== state.eventsPersonId ||
        generation !== state.eventsGeneration
      ) {
        return {};
      }

      return {
        events: [...state.events, ...items],
        eventsCursor: cursor,
        hasMoreEvents: computeHasMorePage(cursor, items),
      };
    }),

  // Never bumps eventsGeneration: that would invalidate an appendEvents already in flight.
  prependEvents: (personId, items) =>
    set((state) => {
      if (personId !== state.eventsPersonId) {
        return {};
      }

      const newEvents = dedupeIncomingEvents(state.events, items);
      if (newEvents.length === 0) {
        return {};
      }

      const events = [...newEvents, ...state.events].sort(
        (a, b) => b.createdAt - a.createdAt,
      );
      // No MAX_LOADED_MESSAGES-equivalent cap for events, so there's no ceiling
      // half to recompute — hasMoreEvents stays whatever pagination decided.
      return {
        events,
      };
    }),

  insertEvent: (personId, event) =>
    set((state) => {
      if (personId !== state.eventsPersonId) {
        return {};
      }

      return {
        events: [...state.events, event].sort(
          (a, b) => b.createdAt - a.createdAt,
        ),
      };
    }),

  updateEvent: (personId, event) =>
    set((state) => {
      if (personId !== state.eventsPersonId) {
        return {};
      }

      return {
        events: state.events.map((e) => (e.id === event.id ? event : e)),
      };
    }),

  removeEvent: (personId, eventId) =>
    set((state) => {
      if (personId !== state.eventsPersonId) {
        return {};
      }

      return {
        events: state.events.filter((e) => e.id !== eventId),
      };
    }),

  setLoadingEvents: (isLoadingEvents) => set({ isLoadingEvents }),

  setLoadingMoreEvents: (isLoadingMoreEvents) => set({ isLoadingMoreEvents }),

  markEventsArrived: (ids) =>
    set((state) => ({
      recentlyInsertedEventIds: addUniqueKeys(
        state.recentlyInsertedEventIds,
        ids,
      ),
    })),

  clearEventArrived: (id) =>
    set((state) => ({
      recentlyInsertedEventIds: state.recentlyInsertedEventIds.filter(
        (existingId) => existingId !== id,
      ),
    })),

  markEventRemovedRemotely: (eventId) =>
    set((state) => ({
      removedEventIds: addUniqueKeys(state.removedEventIds, [eventId]),
      recentlyInsertedEventIds: state.recentlyInsertedEventIds.filter(
        (existingId) => existingId !== eventId,
      ),
    })),

  clearEventRemoved: (eventId) =>
    set((state) => ({
      removedEventIds: state.removedEventIds.filter(
        (existingId) => existingId !== eventId,
      ),
    })),

  markEventRemovedLocally: (eventId) =>
    set((state) => ({
      locallyRemovedEventIds: addUniqueKeys(state.locallyRemovedEventIds, [
        eventId,
      ]),
    })),

  clearEventRemovedLocally: (eventId) =>
    set((state) => ({
      locallyRemovedEventIds: state.locallyRemovedEventIds.filter(
        (existingId) => existingId !== eventId,
      ),
    })),

  setNotesStatus: (notesStatus) => set({ notesStatus }),

  openCreate: () => set({ isCreateOpen: true, editPersonId: null }),

  closeCreate: () => set({ isCreateOpen: false }),

  openEdit: (personId) => set({ editPersonId: personId, isCreateOpen: false }),

  closeEdit: () => set({ editPersonId: null }),

  setDeleteTarget: (personId) => set({ deleteTargetId: personId }),

  setDeepLinkPerson: (deepLinkPerson) => set({ deepLinkPerson }),

  setResolvingDeepLink: (isResolvingDeepLink) => set({ isResolvingDeepLink }),

  clearDeepLink: () => set({ deepLinkPerson: null }),

  reset: () => set(INITIAL_CONTACTS_STATE),
});
